import { Box, CircularProgress, Grid, ThemeProvider } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { CustomExplain } from '../components/CustomExplain'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { LogoAddButton } from '../components/LogoAddButton'
import { LogoList } from '../components/LogoList'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { PicturesPaper } from '../components/PicturesPaper'
import { loadStatus } from '../constants/loadStatus'
import { mediaTheme } from '../constants/theme'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC601LogoGet } from '../hooks/useC601LogoGet'
import { useC602LogoAdd } from '../hooks/useC602LogoAdd'
import { useC603LogoUpdate } from '../hooks/useC603LogoUpdate'
import { useC604LogoDelete } from '../hooks/useC604LogoDelete'
import { useC901SignedUrlGet } from '../hooks/useC901SignedUrlGet'
import { useCloudStorage } from '../hooks/useCloudStorage'
import type { LogoInfo } from '../interfaces/LogoIf'
import { validateFileSize } from '../util/FileSize'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'
import { useDeviceCapability } from '../util/DeviceCapabilityContext'

// ロゴ画面
export const Logo = () => {
  // hooks
  const auth = useAuth()
  const c601 = useC601LogoGet()
  const c602 = useC602LogoAdd()
  const c603 = useC603LogoUpdate()
  const c604 = useC604LogoDelete()
  const c901 = useC901SignedUrlGet()
  const cs = useCloudStorage()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // ローディングステータス監視
  useEffect(() => {
    if (
      c601.loading === loadStatus.LOADING ||
      c602.loading === loadStatus.LOADING ||
      c603.loading === loadStatus.LOADING ||
      c604.loading === loadStatus.LOADING ||
      c901.loading === loadStatus.LOADING ||
      cs.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c601.loading, c602.loading, c603.loading, c604.loading, c901.loading, cs.loading])
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // ロゴ配列
  const [logos, setLogos] = useState<LogoInfo[]>([])

  const {supportsOverwrite} = useDeviceCapability()

  const pattern = /.(jpg|jpeg|png|mp4)$/i
  // 拡張子チェック関数
  const extensionCheck = async (file: string) => {
    const result = pattern.test(file)
    return result
  }

  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      // ロゴ一覧取得
      const result = await c601.execLogoGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          // console.log("execLogoGet");
          setLogos(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ロゴ追加ハンドラ
  const logoAddHandler = async (text: string, file: File) => {
    // ファイルサイズチェック
    if (!validateFileSize(file)) {
      setErrorDialog(errorMsg.FILE_SIZE_ERROR)
      setOpenDialog(true)
      return
    }

    //拡張子チェック
    const result = await extensionCheck(file.name)

    if (result) {
      // 署名付きURL取得
      const resultUrl = await c901.execSignedUrlGet()
      if (resultUrl.success) {
        const resultCs = await cs.execUploadFile(resultUrl.message!, file)
        if (resultCs.success) {
          //ロゴ登録
          const result = await c602.execLogoAdd(text, file, resultUrl.message!, { overwrite: supportsOverwrite })
          if (result.success) {
            // console.log("execLogoAdd.");
            // ロゴ一覧取得
            const result = await c601.execLogoGet(auth.deviceId)
            if (result.success) {
              if (result.info) {
                // console.log("execLogoGet");
                setLogos(result.info!)
              }
            } else {
              setErrorDialog(result.message!)
              setOpenDialog(true)
            }
          } else {
            setErrorDialog(result.message!)
            setOpenDialog(true)
          }
        } else {
          setErrorDialog(resultCs.message!)
          setOpenDialog(true)
        }
      } else {
        setErrorDialog(resultUrl.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
      setOpenDialog(true)
    }
  }
  // ロゴ削除ハンドラ
  const logoDelHandler = async (id: string) => {
    //ロゴ削除
    const result = await c604.execLogoDelete(id)
    if (result.success) {
      // console.log("execLogoDelete.");
      // ロゴ一覧取得
      const result = await c601.execLogoGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          // console.log("execLogoGet");
          setLogos(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // ロゴ編集ハンドラ
  const logoEditHandler = async (oldText: string, newText: string, file?: File) => {
    let url = ''
    if (file) {
      // ファイルサイズチェック
      if (!validateFileSize(file)) {
        setErrorDialog(errorMsg.FILE_SIZE_ERROR)
        setOpenDialog(true)
        return
      }

      //拡張子チェック
      const result = await extensionCheck(file!.name)
      if (result) {
        // 署名付きURL取得
        const resultUrl = await c901.execSignedUrlGet()
        if (resultUrl.success) {
          const resultCs = await cs.execUploadFile(resultUrl.message!, file!)
          if (resultCs.success) {
            url = resultUrl.message!
          } else {
            setErrorDialog(resultCs.message!)
            setOpenDialog(true)
            return
          }
        } else {
          setErrorDialog(resultUrl.message!)
          setOpenDialog(true)
          return
        }
      } else {
        setErrorDialog(errorMsg.UNSUPPORTED_FILE_ERROR)
        setOpenDialog(true)
        return
      }
    }
    //ロゴ更新
    const result = await c603.execLogoUpdate(oldText, newText, file, url)
    if (result.success) {
      // console.log("execLogoUpdate.");
      // ロゴ一覧取得
      const result = await c601.execLogoGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          // console.log("execLogoGet");
          setLogos(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <ThemeProvider theme={mediaTheme}>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <CustomTitle>{translation['label_logo']}</CustomTitle>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            {(c601.loading === loadStatus.LOADING ||
              c602.loading === loadStatus.LOADING ||
              c603.loading === loadStatus.LOADING ||
              c604.loading === loadStatus.LOADING ||
              c901.loading === loadStatus.LOADING ||
              cs.loading === loadStatus.LOADING) && <CircularProgress />}
          </Box>
          <Box>
            <CustomExplain>{translation['message_logo_info']}</CustomExplain>
          </Box>
          <Box
            sx={{
              marginTop: '40px',
            }}
          >
            <PicturesPaper>
              <Grid container spacing={2}>
                <Grid item lg={6} xl={4}>
                  <LogoAddButton
                    title={translation['button_title_logo_name']}
                    logos={logos}
                    onAdd={logoAddHandler}
                    overwrite={supportsOverwrite}
                  >
                    {translation['button_add_new']}
                  </LogoAddButton>
                </Grid>{' '}
              </Grid>
              <Grid container spacing={2} mt={2}>
                <LogoList
                  items={logos}
                  onEditLogo={(oldText, newText, file) => logoEditHandler(oldText, newText, file)}
                  onDeleteLogo={id => logoDelHandler(id)}
                />
              </Grid>
            </PicturesPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </ThemeProvider>
  )
}
